import { Popover } from '@chakra-ui/react';
import React, { useCallback, useId, useMemo, useState } from 'react';
import { useFilterGroup } from './FilterGroupContext';
import type { FilterButtonContextValue } from './useFilterButtonContext';
import {
  FilterButtonProvider,
  useFilterButtonContext as useUnsafeFilterButtonContext,
} from './useFilterButtonContext';

export enum PopoverPlacement {
  Bottom = 'bottom',
  BottomStart = 'bottom-start',
  BottomEnd = 'bottom-end',
  Top = 'top',
  TopStart = 'top-start',
  TopEnd = 'top-end',
  Right = 'right',
  RightStart = 'right-start',
  RightEnd = 'right-end',
  Left = 'left',
  LeftStart = 'left-start',
  LeftEnd = 'left-end',
}

interface PositioningOptions {
  placement?: PopoverPlacement;
}

interface FilterButtonProps {
  children: React.ReactNode;
  isFilterActive: boolean;
  onClearFilter: () => void;
  positioning?: PositioningOptions;
  onInteractOutside?: (event: Event) => void;
  onClose?: () => void;
}

export const useFilterButtonContext = useUnsafeFilterButtonContext;

export const FilterButton: React.FC<FilterButtonProps> = ({
  children,
  isFilterActive,
  onClearFilter,
  positioning = { placement: PopoverPlacement.BottomStart },
  onInteractOutside,
  onClose,
}) => {
  const filterId = useId();
  const filterGroup = useFilterGroup();

  const [localOpen, setLocalOpen] = useState(false);
  const isOpen = filterGroup ? filterGroup.openFilterId === filterId : localOpen;

  const setIsOpen = useCallback(
    (open: boolean) => {
      if (filterGroup) {
        filterGroup.setOpenFilterId(open ? filterId : null);
      } else {
        setLocalOpen(open);
      }
    },
    [filterGroup, filterId]
  );

  const handleClear = useCallback(() => {
    onClearFilter();
    setIsOpen(false);
  }, [onClearFilter, setIsOpen]);

  const handleOpenChange = useCallback(
    (details: { open: boolean }) => {
      if (filterGroup && !details.open && filterGroup.openFilterId !== filterId) {
        return;
      }
      setIsOpen(details.open);
      if (!details.open) {
        onClose?.();
      }
    },
    [setIsOpen, filterGroup, filterId, onClose]
  );

  const contextValue = useMemo<FilterButtonContextValue>(
    () => ({
      isFilterActive,
      onClear: handleClear,
      closePopover: () => setIsOpen(false),
    }),
    [isFilterActive, handleClear, setIsOpen]
  );

  return (
    <FilterButtonProvider value={contextValue}>
      <Popover.Root
        open={isOpen}
        onOpenChange={handleOpenChange}
        positioning={positioning as any}
        onInteractOutside={onInteractOutside}
      >
        {children}
      </Popover.Root>
    </FilterButtonProvider>
  );
};
